import { filter, map, mapTo, mergeMap } from 'rxjs/operators'
import * as ReduxObservable from 'redux-observable'
import * as Router from '../../router'
import * as App from '../../app'
import * as Actions from './actions'
import * as Constants from './constants'
import * as Request from '../../request'
import * as Common from '../common'
import { of } from 'rxjs'
import queryString from 'query-string'

// Simple global cache to prevent duplicate API calls
const storeCallCache = new Set<string>();

// Single epic to handle everything - triggered by custom action
export const surveyResultsMainEpic: ReduxObservable.Epic = (action$, state$, { history }) =>
  action$.ofType(Actions.LOAD_SURVEY_RESULTS_PAGE).pipe(
    map((action: Actions.LoadSurveyResultsPageAction) => {
      const { communityIdentifier, surveyId, token, surveyOptionId } = action.payload;
      
      if (!communityIdentifier || !surveyId) {
        return of();
      }
      
      const actions = [
        { type: Actions.INITIALIZE_SURVEY_RESULTS_FROM_URL, payload: { communityIdentifier, surveyId, token, surveyOptionId } },
        Common.fetchConfiguration(communityIdentifier),
        Actions.fetchSurveyResults(surveyId, communityIdentifier)
      ];
      
      // Add store action only if we have token and haven't already stored for this combination
      if (token && surveyOptionId) {
        const storeKey = `${token}|${surveyOptionId}`;
        
        if (!storeCallCache.has(storeKey)) {
          storeCallCache.add(storeKey);
          actions.push(Actions.storeSurveyResult(token, parseInt(surveyOptionId), communityIdentifier));
        }
      }
      
      return of(...actions);
    }),
    mergeMap(actions => actions)
  );

export const readyForRender: ReduxObservable.Epic = (action$, state$, { history }) =>
  action$.ofType(Request.successConstant(Actions.FETCH_SURVEY_RESULTS), Request.errorConstant(Actions.FETCH_SURVEY_RESULTS))
    .pipe(
      filter(() => 
        history.location.pathname.startsWith(Constants.URL_HOME) ||
        history.location.pathname.startsWith(Constants.URL_RESULTS_VIEW)
      ),
      mapTo(App.readyForRender())
    );

export const handleRedirectAfterSubmission: ReduxObservable.Epic = (action$, state$, { history }) => {
  return action$.ofType(Request.successConstant(Actions.STORE_SURVEY_RESULT), Request.errorConstant(Actions.STORE_SURVEY_RESULT))
    .pipe(
      filter((action: any) => {
        const pathMatches = history.location.pathname.startsWith(Constants.URL_HOME) ||
                           history.location.pathname.startsWith(Constants.URL_RESULTS_VIEW);
        
        if (!pathMatches) {
          return false;
        }
        
        // For error actions, handle "already exists" errors for redirect
        if (action.type === Request.errorConstant(Actions.STORE_SURVEY_RESULT)) {
          const errorMessage = action.payload?.error?.response?.message || 
                               action.payload?.response?.message || 
                               action.payload?.message;
          const isAlreadyExistsError = errorMessage && errorMessage.includes('already exists');
          return isAlreadyExistsError;
        }
        
        // For success actions, always check for redirect
        return true;
      }),
      map(() => {
        const surveyResults = state$.value.surveyResults;
        const hideResults = surveyResults.survey?.hide_results === true;
        const finalUrl = surveyResults.survey?.final_url;
        const userSelectedOptionId = surveyResults.surveyOptionId;
        const hasToken = surveyResults.token;
        
        // If results should be hidden and final_url exists, dispatch redirect action
        if (hideResults && userSelectedOptionId && finalUrl) {
          return Actions.redirectToFinalUrl(finalUrl);
        }
        
        // Return empty action if no redirect needed
        return { type: 'NO_REDIRECT_NEEDED' };
      })
    );
};

export const handleRedirectAction: ReduxObservable.Epic = (action$) =>
  action$.ofType(Actions.REDIRECT_TO_FINAL_URL)
    .pipe(
      map((action: Actions.RedirectToFinalUrlAction) => {
        // Check if we're in browser environment more thoroughly
        const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined' && window.document === document;
        
        if (isBrowser) {
          try {
            const { finalUrl } = action.payload;
            if (finalUrl && finalUrl.trim()) {
              const redirectUrl = finalUrl.startsWith('http') ? finalUrl : `https://${finalUrl}`;
              
              // Try multiple redirect methods
              if (window.location) {
                window.location.href = redirectUrl;
              } else if (document.location) {
                document.location.href = redirectUrl;
              } else {
                // Fallback: use location object directly
                location.href = redirectUrl;
              }
            }
          } catch (error) {
            console.error('Error during redirect:', error);
          }
        }
        
        return { type: 'REDIRECT_COMPLETED' };
      })
    );

export const epic = ReduxObservable.combineEpics(
  surveyResultsMainEpic,
  readyForRender,
  handleRedirectAfterSubmission,
  handleRedirectAction
);